import { useEffect, useState } from 'react'
import { ListFilter, Search, X } from 'lucide-react'
import { useV1ReportingPollResponseOptionsRetrieve } from '@/api/client'
import {
  EMPTY_POLL_RESPONSE_REPORT_FILTERS,
  POLL_RESPONSE_FILTER_ALL,
  type PollResponseReportFilterValues,
} from '@/components/reporting/poll-response-report-filter-values'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { queryHasHttpError } from '@/lib/query-state'
import { reportFilterOptionLabel } from '@/lib/reporting-budget-entries'

const ALL = POLL_RESPONSE_FILTER_ALL

type FilterKey = keyof PollResponseReportFilterValues

interface FilterChip {
  key: FilterKey
  label: string
}

interface PollResponsesReportFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  filters: PollResponseReportFilterValues
  onFiltersChange: (filters: PollResponseReportFilterValues) => void
}

function activeFilterCount(filters: PollResponseReportFilterValues): number {
  return Object.entries(filters).filter(([key, value]) => {
    if (key === 'dateFrom' || key === 'dateTo') return value !== ''
    return value !== ALL
  }).length
}

function formatFilterDate(value: string): string {
  const [year, month, day] = value.split('-')
  return year && month && day ? `${day}.${month}.${year}` : value
}

export function PollResponsesReportFilters({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
}: PollResponsesReportFiltersProps) {
  const [searchInput, setSearchInput] = useState(search)
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(filters)

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== search) onSearchChange(searchInput)
    }, 300)
    return () => clearTimeout(timeout)
  }, [onSearchChange, search, searchInput])

  const optionsQuery = useV1ReportingPollResponseOptionsRetrieve()
  const options = optionsQuery.data?.status === 200 ? optionsQuery.data.data : undefined
  const optionsFailed = queryHasHttpError(optionsQuery)

  const schools = options?.schools ?? []
  const draftSchoolId = draft.schoolId === ALL ? undefined : Number(draft.schoolId)
  // Ankiety only ever list schools/classes that actually occur in responses —
  // a short, complete dictionary, unlike the budget report's paginated lookup.
  const classes = (options?.classes ?? []).filter(
    (klass) => draftSchoolId === undefined || klass.school_id === draftSchoolId,
  )

  const optionLabel = (
    entries: ReadonlyArray<{ value: string; label: string; is_active: boolean }>,
    value: string,
  ) => {
    const entry = entries.find((candidate) => candidate.value === value)
    return entry ? reportFilterOptionLabel(entry) : value
  }

  const schoolLabel = (schoolId: string) =>
    schools.find((school) => String(school.id) === schoolId)?.name ?? schoolId
  const classLabel = (classId: string) =>
    (options?.classes ?? []).find((klass) => String(klass.id) === classId)?.name ?? classId

  const chips: FilterChip[] = []
  if (filters.dateFrom)
    chips.push({ key: 'dateFrom', label: `Od ${formatFilterDate(filters.dateFrom)}` })
  if (filters.dateTo) chips.push({ key: 'dateTo', label: `Do ${formatFilterDate(filters.dateTo)}` })
  if (filters.pollId !== ALL) {
    chips.push({ key: 'pollId', label: `Ankieta: ${optionLabel(options?.polls ?? [], filters.pollId)}` })
  }
  if (filters.schoolId !== ALL) {
    chips.push({ key: 'schoolId', label: `Szkoła: ${schoolLabel(filters.schoolId)}` })
  }
  if (filters.classId !== ALL) {
    chips.push({ key: 'classId', label: `Klasa: ${classLabel(filters.classId)}` })
  }
  if (filters.questionType !== ALL) {
    chips.push({
      key: 'questionType',
      label: `Typ pytania: ${optionLabel(options?.question_types ?? [], filters.questionType)}`,
    })
  }
  if (filters.status !== ALL) {
    chips.push({
      key: 'status',
      label: `Status: ${optionLabel(options?.statuses ?? [], filters.status)}`,
    })
  }

  const count = activeFilterCount(filters)
  const isFiltered = count > 0 || search.trim() !== ''

  const patchDraft = (patch: Partial<PollResponseReportFilterValues>) => {
    setDraft((current) => ({ ...current, ...patch }))
  }

  const removeFilter = (key: FilterKey) => {
    const next = {
      ...filters,
      [key]: key === 'dateFrom' || key === 'dateTo' ? '' : ALL,
    }
    if (key === 'schoolId') next.classId = ALL
    onFiltersChange(next)
  }

  const clearAll = () => {
    setSearchInput('')
    onSearchChange('')
    onFiltersChange(EMPTY_POLL_RESPONSE_REPORT_FILTERS)
  }

  const dictionarySelect = (
    id: string,
    label: string,
    value: string,
    onValueChange: (value: string) => void,
    entries: ReadonlyArray<{ value: string; label: string; is_active: boolean }>,
    allLabel: string,
  ) => (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select value={value} onValueChange={onValueChange} disabled={optionsQuery.isLoading || optionsFailed}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectItem value={ALL}>{allLabel}</SelectItem>
            {entries.map((entry) => (
              <SelectItem key={entry.value} value={entry.value}>
                {reportFilterOptionLabel(entry)}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </Field>
  )

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-end">
        <Field className="min-w-0 flex-1 gap-1">
          <FieldLabel htmlFor="poll-report-search" className="sr-only">
            Szukaj w odpowiedziach otwartych
          </FieldLabel>
          <div className="relative w-full md:max-w-xl">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="poll-report-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Szukaj w odpowiedziach otwartych…"
              className="pr-9 pl-8"
            />
            {searchInput ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="absolute top-1/2 right-1 -translate-y-1/2"
                aria-label="Wyczyść wyszukiwanie"
                onClick={() => {
                  setSearchInput('')
                  onSearchChange('')
                }}
              >
                <X />
              </Button>
            ) : null}
          </div>
        </Field>

        <div className="flex flex-wrap items-center gap-2">
          <Dialog
            open={open}
            onOpenChange={(nextOpen) => {
              setDraft(filters)
              setOpen(nextOpen)
            }}
          >
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-label={count > 0 ? `Filtry: aktywnych ${count}` : undefined}
              >
                <ListFilter data-icon="inline-start" />
                {count > 0 ? `Filtry (${count})` : 'Filtry'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
              <form
                className="contents"
                onSubmit={(event) => {
                  event.preventDefault()
                  onFiltersChange(draft)
                  setOpen(false)
                }}
              >
                <DialogHeader>
                  <DialogTitle>Filtry ankiet</DialogTitle>
                  <DialogDescription>
                    Zawęź wyniki po okresie, ankiecie, szkole, klasie, typie pytania i statusie
                    odpowiedzi.
                  </DialogDescription>
                </DialogHeader>

                {optionsQuery.isLoading ? (
                  <p className="text-sm text-muted-foreground" aria-live="polite">
                    Ładowanie słowników filtrów…
                  </p>
                ) : null}
                {optionsFailed ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <FieldError>Nie udało się pobrać słowników filtrów.</FieldError>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void optionsQuery.refetch()}
                    >
                      Spróbuj ponownie
                    </Button>
                  </div>
                ) : null}

                <FieldGroup className="grid gap-4 sm:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="poll-report-date-from">Data od</FieldLabel>
                    <Input
                      id="poll-report-date-from"
                      type="date"
                      max={draft.dateTo || undefined}
                      value={draft.dateFrom}
                      onChange={(event) => patchDraft({ dateFrom: event.target.value })}
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="poll-report-date-to">Data do</FieldLabel>
                    <Input
                      id="poll-report-date-to"
                      type="date"
                      min={draft.dateFrom || undefined}
                      value={draft.dateTo}
                      onChange={(event) => patchDraft({ dateTo: event.target.value })}
                    />
                  </Field>
                  {dictionarySelect(
                    'poll-report-poll',
                    'Ankieta',
                    draft.pollId,
                    (value) => patchDraft({ pollId: value }),
                    options?.polls ?? [],
                    'Wszystkie ankiety',
                  )}
                  {dictionarySelect(
                    'poll-report-question-type',
                    'Typ pytania',
                    draft.questionType,
                    (value) => patchDraft({ questionType: value }),
                    options?.question_types ?? [],
                    'Wszystkie typy',
                  )}
                  <Field>
                    <FieldLabel htmlFor="poll-report-school">Szkoła</FieldLabel>
                    <Select
                      value={draft.schoolId}
                      disabled={optionsQuery.isLoading || optionsFailed}
                      onValueChange={(value) => patchDraft({ schoolId: value, classId: ALL })}
                    >
                      <SelectTrigger id="poll-report-school" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={ALL}>Wszystkie szkoły</SelectItem>
                          {schools.map((school) => (
                            <SelectItem key={school.id} value={String(school.id)}>
                              {school.name ?? school.code ?? `#${school.id}`}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="poll-report-class">Klasa</FieldLabel>
                    <Select
                      value={draft.classId}
                      disabled={optionsQuery.isLoading || optionsFailed}
                      onValueChange={(value) => patchDraft({ classId: value })}
                    >
                      <SelectTrigger id="poll-report-class" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value={ALL}>Wszystkie klasy</SelectItem>
                          {classes.map((klass) => (
                            <SelectItem key={klass.id} value={String(klass.id)}>
                              {klass.name ?? `#${klass.id}`}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </Field>
                  {dictionarySelect(
                    'poll-report-status',
                    'Status odpowiedzi',
                    draft.status,
                    (value) => patchDraft({ status: value }),
                    options?.statuses ?? [],
                    'Wszystkie statusy',
                  )}
                </FieldGroup>

                <DialogFooter className="sm:justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setDraft(EMPTY_POLL_RESPONSE_REPORT_FILTERS)}
                  >
                    Wyczyść pola
                  </Button>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    <DialogClose asChild>
                      <Button type="button" variant="outline">
                        Anuluj
                      </Button>
                    </DialogClose>
                    <Button type="submit">Pokaż wyniki</Button>
                  </div>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {isFiltered ? (
            <Button type="button" variant="ghost" onClick={clearAll}>
              Wyczyść wszystko
            </Button>
          ) : null}
        </div>
      </div>

      {chips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label="Aktywne filtry">
          {chips.map((chip) => (
            <Badge key={chip.key} variant="secondary" asChild>
              <button
                type="button"
                onClick={() => removeFilter(chip.key)}
                aria-label={`Usuń filtr: ${chip.label}`}
                title={chip.label}
                className="h-7 max-w-full min-w-0"
              >
                <span className="truncate">{chip.label}</span>
                <X data-icon="inline-end" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  )
}
